/* @bend-expect quartz-no-create-browser-history */
import { createBrowserHistory } from 'quartz-routing/react-router';
import { InAppPublicOnlyContextType } from 'marketplace-ui-common/types';
import { ProductType } from 'marketplace-ui-common/types/products';
import { getParsedTrackingParams } from 'marketplace-ui-common/utils/trackingParams';
import { getMarketplaceEventProps } from 'marketplace-ui-tracking';
import {
  LockedTrackerInstance,
  TrackerConfig,
} from 'usage-tracker-core/trackerTypes';
import { createContainer } from 'usage-tracker-multi';
import { withSessionReplay } from 'usage-tracker-session-replay';
import { ListingCard } from '../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc';
import { AutocompleteResultItem } from '../../__generated__/chirp/com/hubspot/query/understanding/mkpl/autocomp/rpc/MarketplaceAutocompleteRpc';

function getSessionUrlParams() {
  const trackingParams = getParsedTrackingParams();
  return Object.keys(trackingParams).reduce((final: string[], key) => {
    const value = trackingParams[key];
    final.push(`${key}=${value}`);
    return final;
  }, []);
}

let trackContainer: ReturnType<typeof createContainer>;
let tracker: LockedTrackerInstance<TrackerConfig & { standalone: true }>;

export function createStandaloneTracker({
  appContext,
  history,
  referrer,
}: {
  appContext: InAppPublicOnlyContextType;
  history: ReturnType<typeof createBrowserHistory>;
  referrer?: string;
}) {
  if (!trackContainer) {
    trackContainer = createContainer({ history });
  }

  if (!tracker) {
    const baseTracker = trackContainer.createTracker({
      standalone: true,
      trackerType: appContext === 'app' ? 'authed' : 'public',
      properties: getMarketplaceEventProps({
        inAppOrPublic: appContext === 'app' ? 'in-app' : 'public',
        referrer,
        sessionUrlParams: getSessionUrlParams(),
      }),
      lastKnownEventProperties: ['screen'],
    });
    // Session replay's endpoint only exists on app.hubspot.com — calling it
    // from the public storefront 404s and triggers a retry storm, and replay
    // isn't useful for anonymous users anyway.
    tracker =
      appContext === 'app' ? withSessionReplay(baseTracker) : baseTracker;
  }

  return tracker;
}

/**
 * ListingCard Search Result Event Props
 */

export function getSourceIdByCard(card: ListingCard) {
  return card.products.map(
    ({ sourceId, productType }) => `${String(productType)}__${sourceId}`
  );
}

export function getInstalledByCard(card: ListingCard) {
  return Boolean(card.products[0]?.installed);
}

// TODO: add typeguard
export function getProductTypeByCard(card: ListingCard) {
  return card.products.length
    ? (card.products[0].productType as ProductType)
    : undefined;
}

/**
 * AutocompleteResultItem Search Result Event Props
 */

// TODO: add typeguard
export function getProductTypeByAutocompleteResultItem(
  item: AutocompleteResultItem
) {
  return item.products.length
    ? (item.products[0]?.productType as ProductType)
    : undefined;
}

export function getSourceIdByAutocompleteResultItem(
  item: AutocompleteResultItem
) {
  return item.products.map(
    (product) => `${String(product.productType)}__${product.sourceId}`
  );
}

export function getRecommendationTypeByCard(card: ListingCard) {
  const mappedRecommendationSources = card.recommendationSources.map(
    (source) => `${source.sourceName}__${source.version}`
  );

  const mappedHighlights = card.highlights.map((h) =>
    h.sectionType
      ? `highlight__${String(h.source.type)}__${String(h.sectionType)}`
      : `highlight__${String(h.source.type)}`
  );

  return [...mappedRecommendationSources, ...mappedHighlights];
}
